import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, CurrencyDollar, Receipt, Package, Wrench, Question, X, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const SLIDES = [
  { icon: Wrench, title: "Welcome to Job Spoken", body: "Turn a spoken description of your work into a priced, professional invoice in under a minute." },
  { icon: Microphone, title: "1. Record the job", body: "Tap the big mic and describe today's work in plain words — or tap “Type Work Instead.” The AI splits it into tasks, materials, customer and location." },
  { icon: CurrencyDollar, title: "2. Get real local prices", body: "Run rate research and the app pulls current local pricing from the web and recommends a rate per task. Tap any price to adjust it." },
  { icon: Receipt, title: "3. Invoice & get paid", body: "Generate a polished invoice, send a PDF, or share a link where customers pay by card. They only ever see the agreed price." },
  { icon: Package, title: "4. Track costs & history", body: "Scan receipts for real costs, manage inventory, and build property & equipment service history for every repeat customer." },
];

export const WalkthroughTour = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(() => !localStorage.getItem("hv_tour_done"));
  const [i, setI] = useState(0);

  const done = () => { localStorage.setItem("hv_tour_done", "1"); setOpen(false); setI(0); };
  const last = i === SLIDES.length - 1;
  const S = SLIDES[i];
  const Icon = S?.icon;

  return (
    <>
      <button data-testid="tour-help-btn" onClick={() => { setI(0); setOpen(true); }}
        className="fixed bottom-24 right-4 z-30 w-11 h-11 rounded-full bg-zinc-900 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform">
        <Question size={22} weight="bold" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="tour-overlay">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 relative animate-in slide-in-from-bottom-4 duration-300">
            <button data-testid="tour-skip" onClick={done} className="absolute top-4 right-4 text-zinc-400"><X size={22} /></button>

            <div className="w-16 h-16 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center mb-5">
              {Icon && <Icon size={34} weight="fill" />}
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tight text-zinc-900">{S.title}</h2>
            <p className="text-zinc-500 mt-2 leading-relaxed">{S.body}</p>

            <div className="flex gap-1.5 mt-6">
              {SLIDES.map((_, idx) => (
                <div key={idx} data-testid={`tour-dot-${idx}`} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-orange-600" : "w-1.5 bg-zinc-200"}`} />
              ))}
            </div>

            <div className="flex items-center gap-3 mt-6">
              {i > 0 && (
                <Button data-testid="tour-back" variant="ghost" className="h-12 px-4" onClick={() => setI(i - 1)}>
                  <ArrowLeft size={18} />
                </Button>
              )}
              {!last ? (
                <Button data-testid="tour-next" className="flex-1 h-12 font-bold bg-orange-600 hover:bg-orange-700 gap-2" onClick={() => setI(i + 1)}>
                  Next <ArrowRight size={18} weight="bold" />
                </Button>
              ) : (
                <div className="flex-1 flex gap-2">
                  <Button data-testid="tour-setup" variant="outline" className="flex-1 h-12 font-bold" onClick={() => { done(); nav("/onboarding"); }}>
                    Set up service area
                  </Button>
                  <Button data-testid="tour-done" className="flex-1 h-12 font-bold bg-orange-600 hover:bg-orange-700" onClick={done}>
                    Start
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
